import React, {
  useCallback,
  Dispatch,
  SetStateAction,
  useState,
  useMemo
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { DateTime } from 'luxon';
import { isEmpty } from 'lodash';
import { DateRange } from 'types/date-range';
import { CalendarDateWithToday } from 'modules/common/components/CalendarDate';
import { getDateTimeFromIso, MIN_SUPPORTED_DATE } from 'utils/date-time';
import FacetDetailField from '../FacetDetailsField';
import { SearchCriteria } from '../type';
import { dateRangeTag } from './tag';

const useStyles = makeStyles((theme) => ({
  spacer: {
    width: '10%',
    display: 'flex',
    alignSelf: 'center',
    justifyContent: 'center'
  },
  date: {
    marginBottom: theme.spacing(-2.5)
  }
}));

type DateRangeFacetDetailsProps = {
  selected: DateCriterionType[];
  setSelected: Dispatch<SetStateAction<DateCriterionType[]>>;
};

export type DateCriterionType = {
  key: string;
  value: DateTime;
};

export type ErrorInfoType = {
  isError: boolean;
  isStartDateError: boolean;
  isEndDateError: boolean;
  message: string | undefined;
};

export const getStartAndEndFromCriterions = (
  searchCriteria: SearchCriteria
): { start: DateTime | null; end: DateTime | null } => {
  const criterions = searchCriteria.criterions;
  if (isEmpty(criterions)) {
    return { start: null, end: null };
  }
  return getStartAndEndFromDateCriterions(
    criterions[dateRangeTag] as DateCriterionType[]
  );
};

export const getErrorInfoType = (
  dateRange: DateRange,
  errorMessages: Record<string, string>
): ErrorInfoType => {
  const start = getDateTimeFromIso(dateRange.start);
  const end = getDateTimeFromIso(dateRange.end);

  const isStartDateError = (start && !start.isValid) || false;
  const isEndDateError = (end && !end.isValid) || false;
  const inValidDateError = isStartDateError || isEndDateError;
  const startCannotExceedEndError = (start && end && start > end) || false;

  const startDateOutOfRangeError =
    start && MIN_SUPPORTED_DATE && start < MIN_SUPPORTED_DATE;
  const endDateOutOfRangeError =
    end && MIN_SUPPORTED_DATE && end < MIN_SUPPORTED_DATE;

  const outOfDateRangeError =
    (startDateOutOfRangeError || endDateOutOfRangeError) ?? false;
  const isError =
    inValidDateError || startCannotExceedEndError || outOfDateRangeError;
  let errorMessage;
  if (inValidDateError) {
    errorMessage = errorMessages['searchBox.error.invalidDateFormat'];
  } else if (startCannotExceedEndError) {
    errorMessage = errorMessages['searchBox.error.dateValidation'];
  } else if (outOfDateRangeError) {
    errorMessage =
      errorMessages[
        startDateOutOfRangeError
          ? 'searchBox.error.startDateOutOfRange'
          : 'searchBox.error.endDateOutOfRange'
      ];
  }

  return {
    isError:
      inValidDateError || startCannotExceedEndError || outOfDateRangeError,
    message: isError ? errorMessage : undefined,
    isStartDateError:
      isStartDateError ||
      Boolean(startDateOutOfRangeError) ||
      startCannotExceedEndError,
    isEndDateError: isEndDateError || Boolean(endDateOutOfRangeError)
  };
};

export const getStartAndEndFromDateCriterions = (
  dateCriterions?: DateCriterionType[]
): { start: DateTime | null; end: DateTime | null } => {
  const start =
    dateCriterions?.find((criterion) => criterion?.key.includes('start+'))
      ?.value || null;

  const end =
    dateCriterions?.find((criterion) => criterion?.key.includes('end+'))
      ?.value || null;

  return { start, end };
};

export const DateRangeLabel = <FormattedMessage id="search.facets.dateRange" />;

export const DateRangeFacetDetails: React.FC<DateRangeFacetDetailsProps> = ({
  selected,
  setSelected
}: DateRangeFacetDetailsProps) => {
  const classes = useStyles();

  const { start, end } = useMemo(
    () => getStartAndEndFromDateCriterions(selected),
    [selected]
  );

  const [dateRange, setDateRange] = useState<
    Record<'start' | 'end', DateTime | null>
  >({ start, end });

  const onFromDateChange = useCallback(
    (date) => {
      setSelected((prev) =>
        [
          date && {
            key: `start+${(date as DateTime).toISODate()}`,
            value: date as DateTime
          },
          ...prev.filter(({ key }) => key.includes('end+'))
        ].filter((x) => x)
      );

      setDateRange((prev) => ({
        ...prev,
        start: date ? (date as DateTime) : null
      }));
    },
    [setSelected]
  );

  const onToDateChange = useCallback(
    (date) => {
      setSelected((prev) =>
        [
          ...prev.filter(({ key }) => key.includes('start+')),
          date && {
            key: `end+${(date as DateTime).toISODate()}`,
            value: date as DateTime
          }
        ].filter((x) => x)
      );
      setDateRange((prev) => ({
        ...prev,
        end: date ? (date as DateTime) : null
      }));
    },
    [setSelected]
  );

  const startDatePickerProps = useMemo(() => {
    return {
      minDate: MIN_SUPPORTED_DATE,
      ...(dateRange?.end ? { maxDate: dateRange.end } : {})
    };
  }, [dateRange.end]);

  const values = useMemo(
    () => ({
      startDateValue: dateRange?.start || null,
      endDateValue: dateRange?.end || null
    }),
    [dateRange]
  );

  const endMinimumDate = useMemo(
    () =>
      dateRange?.start && dateRange?.start > MIN_SUPPORTED_DATE
        ? dateRange?.start
        : MIN_SUPPORTED_DATE,
    [dateRange?.start]
  );
  const onClearStartDate = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onFromDateChange(null);
    },
    [onFromDateChange]
  );

  const onClearEndDate = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onToDateChange(null);
    },
    [onToDateChange]
  );

  return (
    <FacetDetailField label={DateRangeLabel}>
      <CalendarDateWithToday
        testId={'date-range-start-facet'}
        value={values.startDateValue}
        hasEndAdornment
        showClearEndAdornment
        handleDateChange={onFromDateChange}
        onClearDate={onClearStartDate}
        fullWidth
        inputVariant="standard"
        {...startDatePickerProps}
      />
      <div className={classes.spacer}>&nbsp; &ndash; &nbsp;</div>
      <CalendarDateWithToday
        testId={'date-range-end-facet'}
        value={values.endDateValue}
        hasEndAdornment
        showClearEndAdornment
        handleDateChange={onToDateChange}
        onClearDate={onClearEndDate}
        minDate={endMinimumDate}
        inputVariant="standard"
        fullWidth
      />
    </FacetDetailField>
  );
};

export default DateRangeFacetDetails;
